import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.js';
import { ReportHead, Empty } from '../components/Shell.jsx';
import {
  defaultYear, normalisePrefixInput, prefixProblem, referenceExample, statusSentence, yearOptions,
} from '../lib/generatedReference.js';

const DIRECTION_LABEL = { payment: 'Payments', receipt: 'Receipts' };
const rowKey = (bankAccountId, direction) => `${bankAccountId}:${direction}`;

/**
 * Reference prefixes: the letters a cash book line's generated reference starts
 * with. One per bank account, per direction, per financial year — `PACHQ` is
 * Payments, Absa, Cheque account and `RACHQ` the receipts side — and a prefix
 * is never reusable by another (bank, direction) in that year.
 *
 * The server is the authority on all of it (migration 065): this screen only
 * checks the shape as it is typed, saves one row at a time and shows the API's
 * own refusal. A prefix a line has already used is locked, because renaming it
 * would orphan the references already issued under it.
 *
 * WHERE THE YEARS COME FROM: `GET /api/entities/:id` returns `years` (the
 * entity's financial_year rows). The year the API keys on is the calendar year
 * the financial year ENDS in — `FY2027` ending 2027-02-28 is 2027. That
 * endpoint needs `reports.summary`; where it is refused the options fall back
 * to a range around the year the entity is in now.
 */
export function ReferencePrefixes({ entity }) {
  const [years, setYears] = useState(null);       // [[endYear, label]]
  const [year, setYear] = useState(null);
  const [status, setStatus] = useState(null);
  const [saved, setSaved] = useState([]);          // the rows of GET reference-prefixes
  const [typed, setTyped] = useState({});          // rowKey -> text being typed
  const [rowErr, setRowErr] = useState({});        // rowKey -> message
  const [rowBusy, setRowBusy] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!entity?.id) return;
    const fallback = Number(String(entity.financial_year_end ?? '').slice(0, 4)) || new Date().getUTCFullYear();
    api.get(`/api/entities/${entity.id}`)
      .then((d) => setYears(yearOptions(d.years, fallback)))
      .catch(() => setYears(yearOptions([], fallback)));
  }, [entity?.id, entity?.financial_year_end]);

  useEffect(() => {
    if (years && year === null) setYear(defaultYear(years, entity?.financial_year_end));
  }, [years, year, entity?.financial_year_end]);

  const load = useCallback(() => {
    if (!entity?.id || year === null) return Promise.resolve();
    return Promise.all([
      api.get(`/api/entities/${entity.id}/reference-prefixes/status?financialYear=${year}`),
      api.get(`/api/entities/${entity.id}/reference-prefixes?financialYear=${year}`),
    ]).then(([s, p]) => { setStatus(s); setSaved(p.prefixes); setLoadErr(null); })
      .catch((e) => { setStatus(null); setSaved([]); setLoadErr(e.message); });
  }, [entity?.id, year]);

  useEffect(() => { setStatus(null); setTyped({}); setRowErr({}); setMsg(null); load(); }, [load]);

  const savedFor = (item) => saved.find((p) => p.bankAccountId === item.bankAccountId
    && p.direction === item.direction);

  const save = async (item) => {
    const key = rowKey(item.bankAccountId, item.direction);
    const prefix = typed[key] ?? item.prefix ?? '';
    setRowBusy(key); setMsg(null); setRowErr((e) => ({ ...e, [key]: null }));
    try {
      const r = await api.put(`/api/entities/${entity.id}/reference-prefixes`, {
        bankAccountId: item.bankAccountId, direction: item.direction, financialYear: year, prefix,
      });
      setTyped((t) => { const n = { ...t }; delete n[key]; return n; });
      setMsg(r.created ? `${prefix} saved for ${item.bankCode} ${DIRECTION_LABEL[item.direction]}.`
        : r.changed ? `${item.bankCode} ${DIRECTION_LABEL[item.direction]} changed to ${prefix}.`
          : 'Nothing to change — that prefix is already set.');
      await load();
    } catch (e) {
      // 422 INVALID_PREFIX / NOT_A_BANK_ACCOUNT, 409 REFERENCE_PREFIX_TAKEN /
      // REFERENCE_PREFIX_IN_USE, 404 FINANCIAL_YEAR_NOT_FOUND — the message is
      // the server's sentence, shown against the row it is about.
      setRowErr((er) => ({ ...er, [key]: e.message }));
    } finally { setRowBusy(null); }
  };

  const banner = statusSentence(status);

  return (
    <>
      <ReportHead entity={entity} title="Reference prefixes"
                  sub="The letters each cash book's generated references start with." />

      <div className="note">
        Every cash book line gets a reference automatically — <strong>prefix, month, five-digit
        number</strong>, such as <code>PACHQ0900001</code> — so references are gapless and never typed
        twice. Each <strong>bank account</strong> needs its own prefix for <strong>payments</strong> and
        another for <strong>receipts</strong>, for <strong>each financial year</strong>, and a prefix can
        never be used for anything else in that year. For example <code>PACHQ</code> = Payments Absa
        Cheque and <code>RACHQ</code> = Receipts Absa Cheque. A prefix is 2 to 12 capital letters
        and digits and ends in a letter. Once a line has used a prefix it is locked.
      </div>

      <div className="controls">
        <label className="field"><span>Financial year</span>
          <select value={year ?? ''} disabled={!years}
                  onChange={(e) => setYear(Number(e.target.value))}>
            {(years ?? []).map(([y, label]) => <option key={y} value={y}>{label} (ends {y})</option>)}
          </select>
        </label>
      </div>

      {loadErr && <div className="error">{loadErr}</div>}
      {msg && <div className="ok">{msg}</div>}
      {banner && (
        <div className={banner.ok ? 'ok' : 'note note--warn'}>
          {banner.ok ? banner.text : <strong>{banner.text}</strong>}
        </div>
      )}

      {!status && !loadErr ? (
        <div className="empty">Loading…</div>
      ) : status && status.items.length === 0 ? (
        <Empty><p>No account on this company is marked as a bank account.</p></Empty>
      ) : status && (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th>Bank account</th>
                <th style={{ width: 100 }}>Direction</th>
                <th style={{ width: 170 }}>Prefix</th>
                <th>Example</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {status.items.map((item) => {
                const key = rowKey(item.bankAccountId, item.direction);
                const row = savedFor(item);
                const inUse = Boolean(row?.inUse);
                const text = typed[key] ?? item.prefix ?? '';
                const problem = text === '' ? null : prefixProblem(text);
                const example = referenceExample(text);
                const dirty = text !== (item.prefix ?? '');
                return (
                  <tr key={key}>
                    <td><span className="code figure">{item.bankCode}</span> {item.bankName}</td>
                    <td>{DIRECTION_LABEL[item.direction]}</td>
                    <td>
                      <input className="input--figure" style={{ width: '100%' }} value={text}
                             maxLength={12} spellCheck={false} autoCapitalize="characters"
                             disabled={inUse || rowBusy === key}
                             aria-label={`${DIRECTION_LABEL[item.direction]} prefix for ${item.bankCode}`}
                             placeholder={item.direction === 'payment' ? 'e.g. PACHQ' : 'e.g. RACHQ'}
                             title={inUse
                               ? 'Locked: lines already carry references with this prefix, so renaming it would orphan them.'
                               : undefined}
                             onChange={(e) => setTyped((t) => ({ ...t, [key]: normalisePrefixInput(e.target.value) }))}
                             onKeyDown={(e) => {
                               if (e.key === 'Enter' && dirty && !problem && text) save(item);
                             }} />
                      {inUse && <div className="row-note">in use — locked</div>}
                    </td>
                    <td className="figure">
                      {example
                        ? <>{example.head}<span style={{ color: 'var(--ink-faint)' }}>{example.tail}</span>
                          {' '}<span className="row-note" style={{ padding: 0 }}>prefix + month + number</span></>
                        : <span className="cashbook__unknown">{item.configured && !dirty ? '' : '—'}</span>}
                      {problem && <div className="row-refusal" style={{ paddingLeft: 0 }}>{problem}</div>}
                      {rowErr[key] && <div className="row-refusal" style={{ paddingLeft: 0 }}>{rowErr[key]}</div>}
                    </td>
                    <td className="num">
                      <button className="icon-btn" onClick={() => save(item)}
                              disabled={inUse || rowBusy !== null || !dirty || !text || Boolean(problem)}>
                        {rowBusy === key ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="note">
        At each new financial year every bank account needs a fresh prefix pair. Until it has one,
        importing a statement into that account is refused, with a message naming the bank, the
        direction and the year.
      </div>
    </>
  );
}
